// THE LIVE-PREVIEW STREAM LIFECYCLE — open a preview for one run, and reconcile it to the durable record
// when it ends. Split out of useChat.ts for the file-size ceiling, exactly like chat-stream-retry.ts and
// chat-wake.ts, and for the same reason: the controller should read as transport + view-state, not as the
// stream state machine. It owns NO conversation logic and authors NO turn (P-SELF) — the preview text comes
// only from the broker's guard-cleared events, and the terminal state comes only from the recorded snapshot.
//
// Its stores are declared STRUCTURALLY (the chat-stream-retry.ts pattern) so `useChat`'s own bundle passes
// straight in without either file importing the other's shape.

import {
  appendStreamText,
  emptyChatStream,
  reconcileStreamActivity,
  replaceStreamText,
  replaceStreamPlan,
  updateStreamReasoning,
} from './chat-turns.ts';
import type { ChatStreamState, ChatTurn } from './chat-turns.ts';
import {
  clearStreamRetry,
  scheduleStreamRetry,
  STREAM_RETRY_MAX_ATTEMPTS,
} from './chat-stream-retry.ts';
import type { ChatStreamHandlers, SurfaceApi } from './api-client.ts';
import type { ChatTranscriptView } from './projections.ts';
import { notifyTaskProjectionsChanged } from './task-projection-events.ts';

/** ONE canonical empty preview — the value every clear path resets to (identity-compared in setStream). */
export const EMPTY_CHAT_STREAM: ChatStreamState = emptyChatStream();

/** Exactly the transport a live preview may touch: subscribe, and re-read the run it reconciles to. */
type ChatStreamApi = Pick<SurfaceApi, 'getChatTranscript' | 'streamChat'>;

interface MutableCell<T> {
  current: T;
}

/**
 * The controller stores a preview drives. A structural SUBSET of `useChat`'s `ChatStores`: the two
 * supersession guards (§ER #6/#10), the teardown handle, and the three setters a reconcile writes.
 */
export interface ChatStreamStores {
  readonly setStream: React.Dispatch<React.SetStateAction<ChatStreamState>>;
  readonly setTranscript: (view: ChatTranscriptView) => void;
  readonly setLocalTurns: React.Dispatch<React.SetStateAction<readonly ChatTurn[]>>;
  /** Holds the active stream's unsubscribe so a later send / an unmount can tear it down exactly once. */
  readonly streamStopRef: MutableCell<(() => void) | null>;
  /** One bounded reconnect timer when transport reconciliation finds the durable run is still running. */
  readonly streamRetryRef: MutableCell<ReturnType<typeof setTimeout> | null>;
  /** Monotonic generation, bumped per open/interrupt — a reconcile SUPERSEDED by a newer send skips. */
  readonly streamGenRef: MutableCell<number>;
  /** False after unmount — a reconcile skips its post-await setState (§ER #10). */
  readonly mountedRef: MutableCell<boolean>;
}

/** Replace a dead preview with the durable snapshot, then keep following it when the run is not terminal. */
async function reconcileStream(
  api: ChatStreamApi,
  runId: string,
  stores: ChatStreamStores,
  generation: number,
  retryAttempt: number,
): Promise<void> {
  if (stores.streamGenRef.current !== generation) return;
  stores.streamStopRef.current?.();
  stores.streamStopRef.current = null;
  // Keep the last live sentence mounted while the authoritative transcript is in flight. Production can
  // deliver provider-summary / finishing / end in one network chunk; clearing here let React batch all three
  // updates into an empty preview, so the final sentence never painted.
  let view: ChatTranscriptView;
  try {
    view = await api.getChatTranscript(runId);
  } catch {
    // The authoritative read FAILED — a transport blip, or a run this caller may not read PER RUN (that read
    // is still owner-scoped, so a routine's run answers NOT_FOUND even where the SESSION read disclosed it).
    // Clearing the preview here is not cosmetic: an uncleared `stream.runId` pins `streaming` and
    // `runState: 'running'` forever, which latches the thinking row, blocks the composer, AND switches off
    // the idle re-read that is the only remaining way the conversation moves. Before this catch the promise
    // simply rejected unhandled (the caller invokes us with `void`), so the clear below never ran at all.
    if (stores.streamGenRef.current === generation && stores.mountedRef.current) {
      stores.setStream((prev) => (prev.runId === runId ? EMPTY_CHAT_STREAM : prev));
    }
    return;
  }
  if (stores.streamGenRef.current !== generation || !stores.mountedRef.current) return;
  stores.setTranscript(view);
  stores.setLocalTurns((prev) => prev.filter((turn) => turn.pendingRunId !== runId));
  // EOF/refusal can race the durable terminal write. Keep following a still-running record so the UI
  // cannot remain stranded on “thinking” after its original connection has already closed — but only a
  // BOUNDED number of times: the reopen loop had a delay cap and no attempt cap, so a stream that kept
  // dying against a record wedged in `running` pinned the composer in its thinking state forever. After
  // exhaustion the preview clears; the wake poll (chat-wake.ts, unblocked once `stream.runId` is null)
  // keeps re-reading the run and folds the settled answer in, at which point the composer returns.
  if (view.state === 'running') {
    const nextAttempt = retryAttempt + 1;
    if (nextAttempt > STREAM_RETRY_MAX_ATTEMPTS) {
      stores.setStream((prev) => (prev.runId === runId ? EMPTY_CHAT_STREAM : prev));
      return;
    }
    scheduleStreamRetry(stores, generation, nextAttempt, () =>
      openStream(api, runId, stores, nextAttempt),
    );
  } else {
    // Clear the terminal preview before the ancillary Task-projection invalidation. That hint owns no chat
    // state and must never strand the composer if its browser event target is already tearing down.
    stores.setStream((prev) => (prev.runId === runId ? EMPTY_CHAT_STREAM : prev));
    notifyTaskProjectionsChanged();
  }
}

/** Build the preview-only callback set around one generation-checked state mutation helper. */
function streamHandlers(
  runId: string,
  stores: ChatStreamStores,
  generation: number,
  reconcile: () => void,
): ChatStreamHandlers {
  const update = (transform: (current: ChatStreamState) => ChatStreamState): void => {
    stores.setStream((current) =>
      current.runId === runId && stores.streamGenRef.current === generation
        ? transform(current)
        : current,
    );
  };
  return {
    onDelta: (_sequence, text) => update((current) => appendStreamText(current, text)),
    onSnapshot: (text) => update((current) => replaceStreamText(current, text)),
    // Latest-wins: the one Reasoning sentence updates in place instead of accumulating stock banners.
    onReasoning: (text) => update((current) => updateStreamReasoning(current, text)),
    onReasoningSummary: (text) =>
      update((current) => updateStreamReasoning(current, text, 'provider_summary')),
    onPlan: (plan) => update((current) => replaceStreamPlan(current, plan)),
    onRefused: () => update((current) => replaceStreamText(current, '')), // discard the preview
    onEnd: reconcile, // snapshot wins
    onError: reconcile, // fall back to the authoritative read
    // A sanitised activity row streamed live (BUILD-DEC-098) — inserted at publication position; a
    // terminal echo replaces its running part in place so the visible chronology never jumps.
    onActivity: (row) => update((current) => reconcileStreamActivity(current, row)),
  };
}

/**
 * Open the live-preview stream for a just-spawned run and assemble it into the ephemeral view-state. Deltas
 * APPEND to the preview; a late-subscriber snapshot REPLACES it; a refusal DISCARDS it (self-correct). On end
 * or a transport error the stream is torn down, the preview is cleared, and `getChatTranscript` is re-read —
 * the SNAPSHOT WINS (the streamed body is preview-only, never the reconciliation authority). P-SELF holds:
 * the preview text comes only from the broker events, and the terminal state comes from the recorded snapshot.
 */
export function openStream(
  api: ChatStreamApi,
  runId: string,
  stores: ChatStreamStores,
  retryAttempt = 0,
): void {
  const { setStream, streamStopRef, streamGenRef } = stores;
  clearStreamRetry(stores);
  const gen = (streamGenRef.current += 1); // THIS stream's generation — a newer send bumps it and supersedes us
  streamStopRef.current?.(); // tear down any prior stream exactly once before opening a new one
  setStream(emptyChatStream(runId));
  let reconciling = false;
  const reconcile = (): void => {
    if (reconciling) return;
    reconciling = true;
    void reconcileStream(api, runId, stores, gen, retryAttempt);
  };
  streamStopRef.current = api.streamChat(runId, streamHandlers(runId, stores, gen, reconcile));
}
